import { cn } from '@listening/utils/react-helpers';
import CircleCloseIcon from '@listening/assets/icons/close-circled.svg?react';
import CircleCheckIcon from '@listening/assets/icons/gold-circle-check.svg?react';
import LockIcon from '@listening/assets/icons/lock.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import {
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';
import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import { Separator } from '@listening/frontend/components/radix/separator';
import { Link } from '@tanstack/react-router';
import * as m from '@/paraglide/messages';
import { useAtomValue } from '@effect/atom-react';
import { checkoutConfigAtom } from '@listening/frontend/query/user-atoms';
import { AsyncResult } from 'effect/reactivity';
import { formatPrice } from '@/lib/format-price';
import { CustomerTestimonial } from '../marketing/customer-testimonial-carousel';
import type { PremiumGate } from '../misc/providers/premium-dialog-provider';

const headerGate: Record<PremiumGate, () => string> = {
  playback_speed: () => m.dialog_premium_speed(),
  voice: () => m.dialog_premium_voices(),
  upload_limit: () => m.dialog_premium_unlimited(),
  file_type: () => m.dialog_premium_file_types(),
  private_documents: () => m.dialog_premium_private(),
};

export function PremiumDialog({ gate }: { gate: PremiumGate }) {
  const offer = AsyncResult.getOrElse(
    useAtomValue(checkoutConfigAtom({ surface: 'offer' })),
    () => undefined,
  );
  const monthly = offer?.plans.find((plan) => plan.interval === 'month');
  const yearly = offer?.plans.find((plan) => plan.interval === 'year');
  const prices = monthly &&
    yearly && {
      monthlyPrice: formatPrice(monthly.amount_cents / 100, 'usd'),
      yearlyPrice: formatPrice(yearly.amount_cents / 100, 'usd'),
    };
  const trialDays = offer?.trial_days ?? 0;
  return (
    <DialogContent className='flex max-h-[90%] max-w-[820px] flex-col p-0'>
      <ScrollArea>
        <div className='flex flex-col items-center gap-10 p-4'>
          <div className='flex flex-col items-center gap-2'>
            <PremiumDialog.Icon />
            <DialogTitle className='text-center text-[32px] leading-10 font-bold text-[var(--v2-text-primary)]'>
              {headerGate[gate]()}
            </DialogTitle>
            <DialogDescription className='text-sm font-normal text-[var(--v2-text-muted)]'>
              {m.dialog_premium_benefits()}
            </DialogDescription>
          </div>
          <div className='flex gap-6'>
            <div className='max-w-[400px]'>
              <CustomerTestimonial />
            </div>
            <div className='flex flex-col gap-4'>
              <PremiumFeatureRow
                subheading={m.dialog_premium_speed_sub()}
                title={m.dialog_premium_speed_title()}
              />
              <PremiumFeatureRow
                subheading={m.dialog_premium_unlimited_sub()}
                title={m.dialog_premium_unlimited_title()}
              />
              <PremiumFeatureRow
                subheading={m.dialog_premium_voices_sub()}
                title={m.dialog_premium_voices_title()}
              />
              <PremiumFeatureRow
                subheading={m.dialog_premium_types_sub()}
                title={m.dialog_premium_types_title()}
              />
            </div>
          </div>
          <div className='flex w-full max-w-[560px] flex-col items-center gap-2'>
            <Button asChild className='w-full'>
              <Link to='/app/premium'>
                {trialDays > 0
                  ? m.dialog_premium_trial({ count: trialDays })
                  : m.dialog_premium_try()}
              </Link>
            </Button>

            {prices && (
              <p className='text-xs text-[var(--v2-text-muted)]'>
                {trialDays > 0
                  ? m.dialog_premium_pricing_trial(prices)
                  : m.dialog_premium_pricing(prices)}
              </p>
            )}
          </div>
        </div>
      </ScrollArea>
    </DialogContent>
  );
}

function PremiumFeatureRow({
  title,
  subheading,
  last,
}: {
  title: string;
  subheading?: string;
  last?: boolean;
}) {
  return (
    <>
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-2'>
          <CircleCheckIcon />
          <p className='text-xl font-semibold text-[var(--v2-text-primary)]'>
            {title}
          </p>
        </div>
        {subheading && (
          <div className='flex items-center gap-2'>
            <CircleCloseIcon />
            <p className='text-sm text-[var(--v2-text-muted)]'>{subheading}</p>
          </div>
        )}
      </div>
      {!last && <Separator />}
    </>
  );
}

PremiumDialog.Icon = ({ className }: { className?: string }) => (
  <div className='rounded-full bg-[var(--v2-accent)]/5 p-2 dark:bg-white/5'>
    <div className='rounded-full bg-[var(--v2-accent)]/5 p-3 dark:bg-white/15'>
      <LockIcon className={cn('size-6 text-[var(--v2-accent)]', className)} />
    </div>
  </div>
);
